// FAQ (artboard 02).
//
// Strona treści w ramie publicznej: nagłówek strony z „Napisz do nas” po prawej,
// cztery kategorie jako spis po lewej (na 390 pasek przewijany w poziomie) i pytania
// w czterech grupach. Karta kontaktowa na dole zniknęła — droga do skrzynki stoi
// u góry, a dwie drogi do tej samej skrzynki to jedna za dużo (zasada 8).
//
// Rozwijacz to prawdziwy <button> z `aria-expanded` (klawiatura dostaje Enter i spację
// za darmo), a strzałka to systemowy chevron obracany o 180°.
//
// „Skąd klipy” i „Czy muszę umieć montować” są jednym pytaniem (MAPA 37, nowe teksty):
// obie odpowiedzi mówiły o tym samym — że montażu nie ma.

import { useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Link } from 'react-router'
import Icon from '../components/Icon'
import { editWord } from '../locales/plural'
import { FREE_START_EDITS } from '../lib/startBonus'
import Seo from '../components/Seo'
import PublicHeader from '../components/public/PublicHeader'
import { useGone } from '../hooks/useGone'
import PublicFooterBar from '../components/public/PublicFooterBar'
import { usePlans } from '../hooks/useBilling'
import { tokenWord } from '../locales/plural'
import { useI18n } from '../lib/i18n'
import { usePublicPath } from '../lib/publicPaths'
import { scrollToSection, useScrollSpy } from '../lib/useScrollSpy'
import { useEdgeFade } from '../lib/useEdgeFade'
import '../styles/minimal-public.css'

/** Cztery kategorie w kolejności czytania; `id` są kotwicami sekcji i spisu. */
const CATS = [
  { id: 'ogolne', key: 'general', items: ['whatIsEdimi', 'noMiracle', 'whereShots', 'howMany', 'freeStart'] },
  { id: 'tokeny', key: 'tokens', items: ['whatIsToken', 'runOut', 'rollover', 'payMethods', 'withdrawal', 'descriptor'] },
  { id: 'konta', key: 'accounts', items: ['network', 'howPublish', 'howManyAccounts', 'deleteAccount', 'reportContent'] },
  { id: 'plany', key: 'plans', items: ['changePlan', 'cancel'] },
] as const

/**
 * Domyślnie rozwinięte: pytanie, które zmienia kryteria i niesie postawę (film o czterech
 * mechanizmach, M2+M3, 27.09). Wcześniej: pliki po usunięciu konta (artboard 02).
 */
const OPEN_BY_DEFAULT = 'noMiracle'

// Wytłuszczenia żyją w locale jako pary {b0}…{b1}: tłumacz przesuwa je razem ze
// zdaniem, zamiast dostawać do przetłumaczenia goły fragment.
function richText(s: string): ReactNode {
  const parts = s.split(/\{b\d+\}/)
  return <>{parts.map((part, i) => (part ? (i % 2 === 1 ? <b key={i}>{part}</b> : <span key={i}>{part}</span>) : null))}</>
}

function richLink(s: string, to: string, label: string): ReactNode {
  const [before, after = ''] = s.split('{link}')
  return <>{richText(before)}<Link to={to}>{label}</Link>{richText(after)}</>
}

function QA({ q, a, defaultOpen = false }: { q: string; a: ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className={`mp-fq${open ? ' is-open' : ''}`}>
      <button type="button" className="mp-fq-q" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span>{q}</span>
        <span className="mp-chev" aria-hidden="true"><Icon name="ChevronDown" size={20} /></span>
      </button>
      {open && <div className="mp-fq-a">{a}</div>}
    </div>
  )
}

export default function Faq() {
  const { t, locale } = useI18n()
  const pub = usePublicPath()
  const activeId = useScrollSpy(CATS.map((c) => c.id))
  // Reguła 60 (PL1): na 390 spis kategorii jest paskiem przewijanym — zanika po stronie,
  // po której zostały kategorie, a aktywna (ze śledzenia przewijania) staje w widoku.
  const catsRef = useRef<HTMLElement>(null)
  // Przycisk pod odpowiedziami i przyklejony nagłówek to ta sama akcja: gdy ten na dole
  // stoi na ekranie, nagłówek trzyma obrys (jeden pełny przycisk, jak strona główna, VB-10).
  const ctaRef = useRef<HTMLAnchorElement>(null)
  const ctaGone = useGone(ctaRef, true)
  useEdgeFade(catsRef, activeId)
  // Koszt czynności to LICZBA Z SERWERA, ta sama, którą rysuje Cennik (`gem_costs`).
  // Wpisana do locale rozjeżdżała się ze stawką, którą serwer naprawdę nalicza, więc
  // dwie strony tego samego serwisu podawały przed zakupem dwie różne ceny.
  const plans = usePlans()

  /** „3 tokeny” — liczba z API razem z odmianą; `null`, gdy serwer stawki nie podał. */
  const rate = (n: number | undefined): string | null =>
    typeof n === 'number' && Number.isFinite(n) ? `${n} ${tokenWord(locale, n)}` : null

  /**
   * „Czym jest token?” — zdanie ze stawkami, ale TYLKO gdy wszystkie trzy przyszły
   * z API. Brak choćby jednej znaczy „nie wiemy”: wtedy zdanie stoi bez liczb
   * i odsyła do Cennika, zamiast obiecywać cenę z pamięci frontu.
   */
  const tokenAnswer = (): ReactNode => {
    const costs = plans.data?.gem_costs
    const [render, publish, cut] = [rate(costs?.render), rate(costs?.publish), rate(costs?.cut)]
    if (!render || !publish || !cut) {
      return richLink(t('faq.items.whatIsToken.aNoPrices'), pub('/pricing'), t('faq.items.whatIsToken.link'))
    }
    return richText(t('faq.items.whatIsToken.a', { render, publish, cut }))
  }

  const answer = (key: string): ReactNode =>
    key === 'reportContent'
      ? richLink(t('faq.items.reportContent.a'), pub('/zgloszenie'), t('faq.items.reportContent.link'))
      : key === 'whatIsToken'
        ? tokenAnswer()
        // Liczba darmowych editów z tej samej stałej co mikrotekst pod przyciskiem niżej.
        : key === 'freeStart'
          ? richText(t('faq.items.freeStart.a', { n: FREE_START_EDITS, edits: editWord(locale, FREE_START_EDITS) }))
          : richText(t(`faq.items.${key}.a`))

  return (
    <div className="mp">
      <Seo page="faq" />
      <PublicHeader active="faq" quietCta={!ctaGone} />

      <main className="mp-doc">
        <div className="mp-doc-head">
          <h1>{t('faq.hero.title')}</h1>
          <a className="mn-lnk" href="mailto:kontakt@edimi.ai">
            {t('faq.hero.leadLink')}<Icon name="ArrowRight" size={16} />
          </a>
        </div>

        <div className="mp-doc-grid">
          <nav className="mp-cats" ref={catsRef} aria-label={t('legal.toc.label')}>
            {CATS.map((c) => (
              <a key={c.id} href={`#${c.id}`}
                 className={`mp-nav-link${activeId === c.id ? ' is-active' : ''}`}
                 aria-current={activeId === c.id ? 'true' : undefined}
                 onClick={(e) => { e.preventDefault(); scrollToSection(c.id) }}>
                {t(`faq.cat.${c.key}`)}
              </a>
            ))}
          </nav>

          <div>
            {CATS.map((c) => (
              <section className="mp-fq-group" id={c.id} key={c.id}>
                <h2>{t(`faq.cat.${c.key}`)}</h2>
                {c.items.map((key) => (
                  <QA key={key} q={t(`faq.items.${key}.q`)} a={answer(key)} defaultOpen={key === OPEN_BY_DEFAULT} />
                ))}
              </section>
            ))}
            {/* Droga do akcji stoi TAM, GDZIE KOŃCZY SIĘ CZYTANIE (brief 37-R3): kto
                przeczytał odpowiedzi, nie ma wracać na górę strony po przycisk. */}
            <div className="mp-fq-cta">
              <Link to="/register" className="btn btn-primary" data-cta="faq" ref={ctaRef}>{t('landing.nav.startFree')}</Link>
              <small>{t('landing.hero.micro', { n: FREE_START_EDITS, edits: editWord(locale, FREE_START_EDITS) })}</small>
            </div>
          </div>
        </div>
      </main>

      <PublicFooterBar />
    </div>
  )
}
